/**
 * TUXEDO FINAL — the second look, held once.
 *
 * `_design.md` records ONE theme per app and that theme is Bouquet Final, which the generator
 * writes into `global.css`, `design-tokens.js` and the rest. Tuxedo is the sibling the app also
 * ships, switchable in Settings, and it has nowhere in the design file to live — so it lives here,
 * and here only.
 *
 * Two consumers read this one table:
 *
 *   - `scripts/gen-theme.mjs` imports it and emits the two `[data-look='tuxedo']` CSS blocks
 *     INSIDE the generated markers, which is what the web reads. (Node strips the types on the
 *     way in, so this file must import nothing — a `react-native` import here would break the
 *     generator, which runs before any bundler.)
 *   - `App.tsx` turns the right half of it into a NativeWind `vars()` table on the app root View,
 *     which is what native reads. A phone has no `data-look` and no stylesheet to attach one to.
 *
 * So the CSS and the native variable table cannot drift: there is one copy of every value and two
 * renderings of it.
 *
 * The values are the concept sheet's own, converted hex -> "H S% L%" so Tailwind's alpha modifier
 * works (`bg-gold/15` becomes `hsl(var(--gold) / 0.15)`). Where the sheet drew an rgba over a
 * surface, the opaque composite is used — a token has to be a colour a native View can take.
 *
 * Bouquet is deliberately absent. It is the bare `:root` / `.dark:root` the generator writes and
 * the bare native tree with no vars over it, which is why a phone gets the default look for free.
 */

/** Every colour role the generated theme names — the two tables below carry all of them. */
export type Role = keyof (typeof TUXEDO)['light'];

export const TUXEDO = {
  light: {
    'background': '0 0% 98%',
    'foreground': '0 0% 2%',
    'card': '0 0% 100%',
    'card-foreground': '0 0% 2%',
    'card-2': '240 3.2% 93.9%',
    'popover': '224 24.6% 12%',
    'popover-foreground': '0 0% 98%',
    'primary': '354.8 65.8% 44.7%',
    'primary-foreground': '0 0% 100%',
    'secondary': '0 0% 100%',
    'secondary-foreground': '0 0% 2%',
    'secondary-line': '0 0% 78%',
    'muted': '240 3.2% 93.9%',
    'muted-foreground': '218.6 10% 27.5%',
    'accent': '240 3.2% 93.9%',
    'accent-foreground': '0 0% 2%',
    'destructive': '354.8 65.8% 44.7%',
    'destructive-foreground': '0 0% 100%',
    'border': '0 0% 90.2%',
    'input': '0 0% 90.2%',
    'line-strong': '0 0% 78%',
    'ring': '34.4 85.4% 40.4%',
    'success': '34.4 94.2% 26.9%',
    'warning': '34.4 94.2% 26.9%',
    'gradient-from': '224 24.6% 12%',
    'gradient-to': '224 24.6% 12%',
    'band-top': '224 24.6% 12%',
    'band-foot': '224 24.6% 12%',
    'band-ink': '0 0% 98%',
    'band-rule': '34.4 85.4% 40.4%',
    'gold': '34.4 85.4% 40.4%',
    'gold-hot': '34.4 85.4% 40.4%',
    'gold-text': '34.4 94.2% 26.9%',
    'gold-edge': '34.4 94.2% 26.9%',
    'gold-ink': '224 24.6% 12%',
    'dock': '224 24.6% 12%',
    'dock-ink': '0 0% 98%',
    'dock-muted': '220 15.4% 65.7%',
    'dock-on': '0 0% 100%',
    'dock-rule': '34.4 85.4% 40.4%',
    'dock-line': '222.9 12.3% 22.4%',
    'dock-badge': '354.8 65.8% 44.7%',
    'dock-badge-ink': '0 0% 100%',
    'track': '0 0% 90.2%',
    'knob': '0 0% 100%',
    'switch-on': '218.6 10% 27.5%',
    'plant': '218.6 10% 27.5%',
    'plant-2': '218.6 10% 27.5%',
    'plant-pot': '240 3.2% 93.9%',
    'points-bg': '224 24.6% 12%',
    'points-ink': '36.4 61.7% 53.9%',
    'points-line': '36.4 61.7% 53.9%',
    'seg-line': '0 0% 78%',
    'seg-on': '224 24.6% 12%',
    'seg-on-ink': '0 0% 98%',
    'seg-off-ink': '218.6 10% 27.5%',
    'bulb-core': '39 67.4% 65.1%',
    'bulb-on': '34.4 85.4% 40.4%',
    'bulb-off': '34.4 85.4% 40.4%',
    'meter-fill': '354.8 65.8% 44.7%',
    'meter-fill-hot': '354.8 65.8% 44.7%',
  },
  dark: {
    'background': '224 24.6% 12%',
    'foreground': '0 0% 98%',
    'card': '219 25% 15.7%',
    'card-foreground': '0 0% 98%',
    'card-2': '219.1 23.7% 19%',
    'popover': '224 24.6% 12%',
    'popover-foreground': '0 0% 98%',
    'primary': '354.8 65.8% 44.7%',
    'primary-foreground': '0 0% 100%',
    'secondary': '219 25% 15.7%',
    'secondary-foreground': '0 0% 98%',
    'secondary-line': '217.5 9.3% 33.7%',
    'muted': '219.1 23.7% 19%',
    'muted-foreground': '220 15.4% 65.7%',
    'accent': '219.1 23.7% 19%',
    'accent-foreground': '0 0% 98%',
    'destructive': '354.8 65.8% 44.7%',
    'destructive-foreground': '0 0% 100%',
    'border': '216.7 13.8% 25.5%',
    'input': '216.7 13.8% 25.5%',
    'line-strong': '217.5 9.3% 33.7%',
    'ring': '36.4 61.7% 53.9%',
    'success': '36.4 61.7% 53.9%',
    'warning': '36.4 61.7% 53.9%',
    'gradient-from': '220 23.9% 22.2%',
    'gradient-to': '220 23.9% 22.2%',
    'band-top': '220 23.9% 22.2%',
    'band-foot': '220 23.9% 22.2%',
    'band-ink': '0 0% 98%',
    'band-rule': '34.4 85.4% 40.4%',
    'gold': '34.4 85.4% 40.4%',
    'gold-hot': '34.4 85.4% 40.4%',
    'gold-text': '36.4 61.7% 53.9%',
    'gold-edge': '34.4 94.2% 26.9%',
    'gold-ink': '224 24.6% 12%',
    'dock': '224 24.6% 12%',
    'dock-ink': '0 0% 98%',
    'dock-muted': '220 15.4% 65.7%',
    'dock-on': '0 0% 100%',
    'dock-rule': '34.4 85.4% 40.4%',
    'dock-line': '222.9 12.3% 22.4%',
    'dock-badge': '354.8 65.8% 44.7%',
    'dock-badge-ink': '0 0% 100%',
    'track': '224 24.6% 12%',
    'knob': '0 0% 98%',
    'switch-on': '218.6 10% 27.5%',
    'plant': '220 15.4% 65.7%',
    'plant-2': '220 15.4% 65.7%',
    'plant-pot': '219.1 23.7% 19%',
    'points-bg': '220 23.9% 22.2%',
    'points-ink': '36.4 61.7% 53.9%',
    'points-line': '36.4 61.7% 53.9%',
    'seg-line': '217.5 9.3% 33.7%',
    'seg-on': '0 0% 98%',
    'seg-on-ink': '224 24.6% 12%',
    'seg-off-ink': '220 15.4% 65.7%',
    'bulb-core': '54.2 100% 93.9%',
    'bulb-on': '40.3 81.4% 83.1%',
    'bulb-off': '40.3 81.4% 83.1%',
    'meter-fill': '354.8 65.8% 44.7%',
    'meter-fill-hot': '354.8 65.8% 44.7%',
  },
} as const;

/**
 * The custom-property table for a look and mode, or null when there is nothing to override.
 *
 * Keys carry the `--` prefix because that is what both `vars()` overloads want. Null for bouquet
 * rather than an empty object: an empty `vars()` table is still a style entry the interop has to
 * diff on every render, and bouquet is the case that should cost nothing.
 */
export function lookVars(look: 'bouquet' | 'tuxedo', mode: 'light' | 'dark'): Record<`--${string}`, string> | null {
  if (look !== 'tuxedo') return null;
  const table = TUXEDO[mode];
  const out: Record<string, string> = {};
  for (const [role, value] of Object.entries(table)) out[`--${role}`] = value;
  return out;
}
